import React from "react";
import { useNavigate } from "react-router-dom";

import { FormattedNumberInput } from "@features/real-estate/components/formatted-number-input";
import { RealEstateToggle } from "@features/real-estate/components/toggle";
import { REAL_ESTATE_PROPERTY_TYPES } from "@features/real-estate/state/constants";
import { useRealEstateWorkspace } from "@features/real-estate/state/workspace";
import { useRealEstateEnrichmentMutation } from "@features/real-estate/queries/enrichment";

const STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

export default function RealEstateIntakeScreen(): React.ReactElement {
  const { state, dispatch } = useRealEstateWorkspace();
  const navigate = useNavigate();
  const enrichmentMutation = useRealEstateEnrichmentMutation();

  const handleNumberChange = (field: keyof typeof state.intake) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value.trim();
    dispatch({
      type: "update_intake",
      patch: {
        [field]: nextValue === "" ? 0 : Number(nextValue),
      },
    });
  };

  const handleValueChange = (field: keyof typeof state.intake) => (value: number) => {
    dispatch({
      type: "update_intake",
      patch: {
        [field]: value,
      },
    });
  };

  const handleTextChange = (field: keyof typeof state.intake) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    dispatch({
      type: "update_intake",
      patch: {
        [field]: event.target.value,
      },
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    dispatch({ type: "apply_intake_to_base" });
    try {
      await enrichmentMutation.mutateAsync();
    } catch {
      // Context screen renders the error state and retry control.
    }
    navigate("/real-estate/context");
  };

  const canSubmit = state.intake.addressLine1.trim() !== "" && state.intake.city.trim() !== "" && state.intake.state.trim() !== "";

  return (
    <section className="re-screen">
      <div className="re-screen-header">
        <div>
          <h2>Deal intake</h2>
          <p>Capture the property facts you already know, then let the workspace normalize public context without saving anything server-side.</p>
        </div>
        {state.redirectNotice ? <div className="re-callout">{state.redirectNotice}</div> : null}
      </div>

      <form className="re-panel re-form-panel" onSubmit={handleSubmit}>
        <div className="re-section-heading">
          <h3>Property</h3>
          <p>Address fields are the only hard requirement for the enrichment pass.</p>
        </div>

        <div className="re-form-grid">
          <label>
            <span>Address line 1</span>
            <input type="text" value={state.intake.addressLine1} onChange={handleTextChange("addressLine1")} placeholder="123 Main St" />
          </label>
          <label>
            <span>Address line 2</span>
            <input type="text" value={state.intake.addressLine2} onChange={handleTextChange("addressLine2")} placeholder="Unit or apt" />
          </label>
          <label>
            <span>City</span>
            <input type="text" value={state.intake.city} onChange={handleTextChange("city")} placeholder="New York" />
          </label>
          <label className="re-select-field">
            <span>State</span>
            <select value={state.intake.state} onChange={handleTextChange("state")}>
              <option value="">Select state</option>
              {STATES.map((stateCode) => (
                <option key={stateCode} value={stateCode}>{stateCode}</option>
              ))}
            </select>
          </label>
          <label>
            <span>ZIP</span>
            <input type="text" value={state.intake.postalCode} onChange={handleTextChange("postalCode")} placeholder="10001" />
          </label>
          <label>
            <span>Listing URL</span>
            <input type="url" value={state.intake.listingUrl} onChange={handleTextChange("listingUrl")} placeholder="Optional reference link" />
          </label>
          <label className="re-select-field">
            <span>Property type</span>
            <select value={state.intake.propertyType} onChange={handleTextChange("propertyType")}>
              {REAL_ESTATE_PROPERTY_TYPES.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <div className="re-toggle-field">
            <span>Light rehab expected</span>
            <RealEstateToggle
              checked={state.intake.lightRehab}
              onChange={(checked) => dispatch({ type: "update_intake", patch: { lightRehab: checked } })}
              onLabel="Expected"
              offLabel="Off"
            />
          </div>
        </div>

        <div className="re-section-heading">
          <h3>Deal facts</h3>
          <p>Use whatever you have right now. You can refine underwriting inputs in the next tab.</p>
        </div>

        <div className="re-form-grid">
          <label>
            <span>Asking price</span>
            <FormattedNumberInput value={state.intake.askingPrice} onValueChange={handleValueChange("askingPrice")} placeholder="1,000,000" />
          </label>
          <label>
            <span>Target offer</span>
            <FormattedNumberInput value={state.intake.targetOffer} onValueChange={handleValueChange("targetOffer")} placeholder="950,000" />
          </label>
          <label>
            <span>Unit count</span>
            <input type="number" min="1" step="1" value={state.intake.unitCount || ""} onChange={handleNumberChange("unitCount")} />
          </label>
          <label>
            <span>Bedrooms</span>
            <input type="number" min="0" step="0.5" value={state.intake.bedrooms || ""} onChange={handleNumberChange("bedrooms")} />
          </label>
          <label>
            <span>Bathrooms</span>
            <input type="number" min="0" step="0.5" value={state.intake.bathrooms || ""} onChange={handleNumberChange("bathrooms")} />
          </label>
          <label>
            <span>Square feet</span>
            <input type="number" min="0" step="1" value={state.intake.squareFeet || ""} onChange={handleNumberChange("squareFeet")} />
          </label>
          <label>
            <span>Year built</span>
            <input type="number" min="0" step="1" value={state.intake.yearBuilt || ""} onChange={handleNumberChange("yearBuilt")} />
          </label>
          <label>
            <span>Annual property tax</span>
            <FormattedNumberInput value={state.intake.annualPropertyTax} onValueChange={handleValueChange("annualPropertyTax")} placeholder="7,200" />
          </label>
          <label>
            <span>Annual insurance</span>
            <FormattedNumberInput value={state.intake.annualInsurance} onValueChange={handleValueChange("annualInsurance")} placeholder="1,800" />
          </label>
        </div>

        <div className="re-action-row">
          <button type="submit" className="re-button re-button-primary" disabled={!canSubmit || enrichmentMutation.isPending}>
            {enrichmentMutation.isPending ? "Loading context..." : "Load context"}
          </button>
          <button type="button" className="re-button" onClick={() => dispatch({ type: "reset_workspace" })}>
            Reset
          </button>
        </div>
      </form>
    </section>
  );
}
